import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import { Link } from "react-router-dom";
import authProvider from "../../../authProvider";
import {
  EyeOpen,
  EyeClosed,
  Mail,
  LoadingIcon,
} from "../../../assets/icons/Icons";
import PrivacyPolicyFooter from "../../../components/form/PrivacyPolicyFooter";
import Logo from "../../../components/Logo";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../../../components/LanguageSwitcher";
import "../../../assets/scss/components/login.scss";
import { useThemeContext } from "../../../contexts/ThemeContext";
import { useGlobalContext } from "../../../contexts/GlobalContext";
import { useUserContext } from "../../../contexts/UserContext";

function Index(props) {
  const { t, i18n } = useTranslation();
  const { theme } = useThemeContext();
  const { setIsAuth } = useGlobalContext();
  const { setUserData } = useUserContext();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isRevealPassword, setIsRevealPassword] = useState(false);
  const [captchaValid, setCaptchaValid] = useState(false);
  const [loginError, setLoginError] = useState("");
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const recaptchaRef = useRef(null);
  const [recaptchaKey, setRecaptchaKey] = useState(0);

  useEffect(() => {
    // 🔄 Force re-render when language changes
    setRecaptchaKey((prev) => prev + 1);
  }, [i18n.language]);
  // Removed localStorage credential storage for PCI/SOC2 compliance
  // rememberMe now controls cookie duration instead

  const handleCaptchaChange = (value) => {
    setCaptchaValid(value);
    setLoginError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // rememberMe controls cookie duration
    // No longer storing credentials in localStorage for security

    if (captchaValid) {
      authProvider
        .login({
          email: email,
          password: password,
          captcha: captchaValid,
          rememberMe: rememberMe, // Pass rememberMe to control cookie duration
        })
        .then(({ mfaData, userData }) => {
          if (mfaData && mfaData.user_sub && mfaData.mfa_required) {
            setLoading(false);
            navigate(
              `/auth/mfa-${mfaData.mfa_registered ? "verify" : "setup"}`
            );
            return;
          }
          authProvider
            .checkAuth()
            .then(() => {
              // Token is now in httpOnly cookie
              setIsAuth(true);
              setUserData(userData);
              setLoading(false);
              setLoginError("");
              navigate("/");
            })
            .catch(() => {
              setUserData(null);
              setLoading(false);
              setCaptchaValid(false);
              setLoginError("Login failed, Authentication invalid!");
              recaptchaRef.current.reset();
            });
        })
        .catch((error) => {
          setLoading(false);
          setCaptchaValid(false);
          setLoginError(error.message || "Login failed");
          recaptchaRef.current.reset();
        });
    } else {
      setLoading(false);
      setLoginError("Invalid captcha");
    }
  };
  return (
    <div>
      <div className="mt-3 text-center">
        <Logo
          className="logo"
          src={theme?.logo}
          style={{ maxWidth: "200px", objectFit: "contain" }}
        />
      </div>
      <div className="login">
        <div className="pl-3 pr-3">
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <LanguageSwitcher />
          </div>
          <div className="mt-3 text-center">
            <h1 className="mb-4">
              {t("Log in to")} {t(`${theme?.copyright_company_name}`)}
            </h1>
          </div>
          <form
            action=""
            method="post"
            className="mb-2"
            onSubmit={handleSubmit}
          >
            <div className="row mb-2">
              <div className="col-xs-12">
                <label htmlFor="email">{t("Email")}</label>
                <div className="input-wrapper input-with-icon input-with-icon-left">
                  <input
                    value={email}
                    type="email"
                    placeholder="john@example.com"
                    name="email"
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <div className="input-icon input-icon-left">
                    <Mail />
                  </div>
                </div>
              </div>
            </div>
            <div className="row mb-2">
              <div className="col-xs-12">
                <label htmlFor="password">{t("Password")}</label>
                <div className="input-wrapper input-with-icon input-with-icon-right">
                  <input
                    placeholder="Enter your password"
                    name="password"
                    type={isRevealPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="on"
                  />
                  <div
                    className="input-icon input-icon-right"
                    onClick={() =>
                      setIsRevealPassword((prevState) => !prevState)
                    }
                  >
                    {isRevealPassword ? <EyeOpen /> : <EyeClosed />}
                  </div>
                </div>
              </div>
            </div>
            <div className="row small-text mt-2 mb-3">
              <div className="col-xs-6">
              </div>
              <div className="col-xs-6 text-right">
                <Link to="/user/forgot-password">{t("Forgot Password?")}</Link>
              </div>
            </div>
            <div className="row">
              <div className="col-xs-12">
                <div className="captcha mb-3">
                  <ReCAPTCHA
                    key={recaptchaKey} // ✅ re-mount when language changes
                    ref={recaptchaRef}
                    sitekey={process.env.REACT_APP_GOOGLE_SITE_KEY}
                    onChange={handleCaptchaChange}
                    onExpired={() => handleCaptchaChange(false)}
                    onError={() => handleCaptchaChange(false)}
                    hl={i18n.language.startsWith("fr") ? "fr" : "en"} // ✅ clean language handling
                  />
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-xs-12">
                <div className="input-wrapper">
                  <button
                    className={`btn btn-primary ${
                      loading ? "btn-loading" : ""
                    }`}
                    style={{ width: "100%" }}
                    type="submit"
                    name="submit"
                  >
                    {loading && <LoadingIcon />}
                    Log In
                  </button>
                </div>
              </div>
            </div>
            {loginError && (
              <div className="text-center">
                <span className="form-error">{loginError}</span>
              </div>
            )}
          </form>
          <PrivacyPolicyFooter />
        </div>
      </div>
    </div>
  );
}

export default Index;
